import ActionButton from "@/components/Button/ActionButton";
import { FillScreen, HStack } from "@/components/Layout";
import SearchInput from "@/components/SearchInput";
import { CertificationType } from "@/constants/certification";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { defaultNS } from "@/lib/i18n";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import usePersonParams from "../../-hooks/usePersonParams";
import { EducationAndQualificationTabs } from "./-components/EducationAndQualificationTabs";
import { CertificationsDataTable } from "./certification/-components/CertificationsDataTable";
import { useCertificationsSearch } from "./certification/-hooks/useCertificationsSearch";
import { EducationAndQualificationSearchSchema } from "./certification/-zod-schema";

export const Route = createFileRoute(
  "/_staff/person/$id/_profile/education-and-qualification/",
)({
  component: EducationAndQualificationList,
  validateSearch: zodValidator(EducationAndQualificationSearchSchema),
});

export function EducationAndQualificationList() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.person.profile.educationsAndQualifications",
  });
  useDocumentTitle(t("title"));

  const { id: personId } = usePersonParams();

  const { search, changeFreeText } = useCertificationsSearch();
  const handleFilterInput = useDebouncedCallback(
    (e: ChangeEvent<HTMLInputElement>) => changeFreeText(e.target.value),
    [changeFreeText],
  );

  const navigate = useNavigate();

  return (
    <FillScreen className="flex flex-col gap-4 px-12 py-5">
      <HStack className="gap-2 items-center">
        <EducationAndQualificationTabs personId={personId} />
        <span className="ml-auto">
          <ActionButton
            icon="PlusIcon"
            className="bg-primary-fill"
            onClick={() =>
              navigate({
                to: "/certifications/add/$certificationType",
                params: {
                  certificationType: CertificationType.CERTIFICATION,
                },
                search: {
                  personId: personId,
                },
              })
            }
          >
            {t("certification.add")}
          </ActionButton>
        </span>
      </HStack>
      <HStack className="justify-between items-center p-2 rounded bg-background">
        <HStack className="gap-2">
          <SearchInput
            key={`search-input-certifications`}
            className="w-80 h-8 shrink-0"
            placeholder={t("certification.filterPlaceholder")}
            defaultValue={search.freeText}
            onChange={handleFilterInput}
          />
        </HStack>
      </HStack>
      <CertificationsDataTable personId={personId} />
    </FillScreen>
  );
}
